/**
 * Nommage des comptes ajoutés à la bibliothèque.
 *
 * Convention du produit : pour les régies publicitaires, le nom du `LibraryItem` porte
 * l'identifiant du compte entre parenthèses — « Adspring (via Agence Adspring)
 * (434-574-0727) ». Ce n'est pas décoratif : la trentaine d'écrans qui listent les
 * comptes n'affichent que `name`, jamais `sourceId`. Un nom sans identifiant rend le
 * compte impossible à distinguer d'un homonyme.
 *
 * Cette règle était appliquée par le flux d'ajout principal (App.js) mais pas par les
 * imports du Kanban, d'où des comptes affichés sans identifiant.
 */

/** Plateformes dont le nom doit porter l'identifiant du compte. */
export const ID_IN_ACCOUNT_NAME = [
  'GOOGLE_ADS',
  'MICROSOFT_ADS',
  'GOOGLE_MERCHANT_API',
  'FACEBOOK_ADS',
  // ChatGPT Ads suit la même règle, et le parc l'applique déjà : le compte Adspring y figure
  // sous « Adspring (adacct_6a90578c…) ». Sans cette entrée, les deux voies d'ajout — le
  // Gestionnaire et « Ajouter des données » — auraient créé des comptes sans identifiant à
  // côté de celui-là, dans la même liste.
  'OPENAI_ADS'
];

/**
 * Met l'identifiant à la forme attendue pour la plateforme.
 * Google Ads s'écrit par groupes de trois — 4345740727 → 434-574-0727 — comme le fait
 * `formatCustomerId` côté backend. Les autres plateformes gardent leur identifiant brut.
 */
export const formatAccountId = (provider, rawId) => {
  const id = String(rawId ?? '');
  if (provider !== 'GOOGLE_ADS') return id;

  const digits = id.replace(/\D/g, '');
  if (digits.length !== 10) return id; // format inattendu : ne rien inventer
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Nom à enregistrer pour un compte.
 * Sans identifiant exploitable, on renvoie le nom seul plutôt qu'un « Nom () » vide.
 */
export const buildAccountName = (provider, baseName, rawId) => {
  const name = (baseName || '').trim();
  if (!ID_IN_ACCOUNT_NAME.includes(provider)) return name;

  const formatted = formatAccountId(provider, rawId);
  if (!formatted) return name;
  // Ne pas doubler l'identifiant s'il est déjà présent dans le nom fourni.
  if (name.includes(formatted)) return name;

  return `${name} (${formatted})`;
};
